import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { postForgotPassword } from '@/auth/api';
import { FormTextField } from '@/components/form/form-text-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AuthCard } from '@/pages/auth/auth-card';
import { applyApiErrors } from '@/pages/auth/form-errors';

const schema = z.object({ email: z.string().email() });
type ForgotValues = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [sent, setSent] = useState(false);

  const form = useForm<ForgotValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await postForgotPassword(values);
      setSent(true);
    } catch (error) {
      // Legacy parity: the broker's reason ("We can't find a user with that
      // email address.", "Please wait before retrying.") lands under the field,
      // so a player who typed the wrong address knows to try another. Hiding it
      // protected nothing - registration already answers "email taken". A
      // failed send or a network error keeps the form for a retry.
      applyApiErrors(error, form.setError, () => toast.error(t('errors.generic')));
    }
  });

  return (
    <AuthCard
      title={t('forgotPassword.title')}
      footer={
        <Link to="/login" className="text-dim hover:text-strong inline-flex items-center gap-1">
          <ArrowLeft className="size-3.5" aria-hidden />
          {t('forgotPassword.backToLogin')}
        </Link>
      }
    >
      {sent ? (
        <p className="font-mono text-sm text-green">{t('forgotPassword.sent')}</p>
      ) : (
        <Form {...form}>
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            <p className="font-mono text-xs text-dim">{t('forgotPassword.prompt')}</p>
            <FormTextField
              control={form.control}
              name="email"
              label={t('fields.email')}
              type="email"
              autoComplete="email"
            />
            <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
              {t('forgotPassword.submit')}
            </Button>
          </form>
        </Form>
      )}
    </AuthCard>
  );
}
